/**
 * Serviço para monitorar o estado de conectividade
 * 
 * Este serviço detecta mudanças na conectividade do dispositivo
 * e fornece APIs para o resto da aplicação reagir a essas mudanças.
 */

// Armazenar callbacks registrados para notificações
const connectivityCallbacks = new Set();

// Estado atual da conectividade
let isOnline = navigator.onLine;
let connectionType = null;
let effectiveConnectionType = null;

// Inicializar a detecção de tipo de conexão (se disponível)
if ('connection' in navigator) {
  connectionType = navigator.connection.type;
  effectiveConnectionType = navigator.connection.effectiveType;
  
  // Ouvir mudanças no tipo de conexão
  navigator.connection.addEventListener('change', handleConnectionChange);
}

/**
 * Handler para mudanças na conexão de rede
 */
function handleConnectionChange() {
  // Atualizar tipo de conexão (se disponível)
  if ('connection' in navigator) {
    connectionType = navigator.connection.type;
    effectiveConnectionType = navigator.connection.effectiveType;
  }
  
  // Verificar estado online/offline
  const wasOnline = isOnline;
  isOnline = navigator.onLine;
  
  // Se houve mudança no estado, notificar os callbacks
  if (wasOnline !== isOnline) {
    notifyCallbacks();
  }
}

/**
 * Notificar todos os callbacks registrados sobre mudança no estado
 */
function notifyCallbacks() {
  const connectionState = getConnectionState();
  connectivityCallbacks.forEach(callback => {
    try {
      callback(connectionState);
    } catch (error) {
      console.error('Erro ao executar callback de conectividade:', error);
    }
  });
}

/**
 * Obter estado atual da conexão
 * @returns {Object} Estado detalhado da conexão
 */
function getConnectionState() {
  return {
    online: isOnline,
    connectionType,
    effectiveType: effectiveConnectionType,
    // Avaliar a qualidade da conexão baseado no tipo efetivo
    quality: getConnectionQuality(effectiveConnectionType)
  };
}

/**
 * Determinar a qualidade da conexão baseado no tipo efetivo
 * @param {string} effectiveType - Tipo efetivo da conexão (slow-2g, 2g, 3g, 4g)
 * @returns {string} Qualidade da conexão (poor, low, medium, high)
 */
function getConnectionQuality(effectiveType) {
  if (!effectiveType) return isOnline ? 'unknown' : 'offline';
  
  switch (effectiveType) {
    case 'slow-2g':
      return 'poor';
    case '2g':
      return 'low';
    case '3g':
      return 'medium';
    case '4g':
      return 'high';
    default:
      return 'unknown';
  }
}

// Registrar para eventos de conectividade do navegador
window.addEventListener('online', handleConnectionChange);
window.addEventListener('offline', handleConnectionChange);

// API pública do serviço
export default {
  /**
   * Verificar se o dispositivo está online
   * @returns {boolean} - true se o dispositivo estiver online
   */
  isOnline: () => isOnline,
  
  /**
   * Obter estado detalhado da conexão
   * @returns {Object} - Detalhes da conexão
   */
  getConnectionState,
  
  /**
   * Registrar um callback para ser notificado de mudanças na conectividade
   * @param {Function} callback - Função a ser chamada quando o estado mudar
   * @returns {Function} - Função para cancelar o registro
   */
  subscribe: (callback) => {
    if (typeof callback !== 'function') {
      throw new Error('O callback deve ser uma função');
    }
    
    // Adicionar callback à lista
    connectivityCallbacks.add(callback);
    
    // Chamar imediatamente com o estado atual
    callback(getConnectionState());
    
    // Retornar função para cancelar registro
    return () => {
      connectivityCallbacks.delete(callback);
    };
  }
}; 